import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';

import { useSmartBack } from '../../shared/navigation';
import {
  addPODocuments,
  createPoGRN,
  deletePODocument,
  getOrderByOrderId,
  getPoGRNs,
  uploadFilesToS3
} from '../services/api/implementation/impl';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Loader from '../../shared/components/ui/Loader';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Modal from '../../shared/components/ui/Modal';
import {
  MAX_DOCUMENT_MB,
  PO_DOCUMENT_TYPES,
  uploadPODocumentFiles,
  validatePODocumentFiles
} from '../utils/poDocuments';

const OrderItemsList = () => {
  const userId = Cookies.get('userId');
  const { collapsed } = useSidebar();
  const { id } = useParams();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory/orders' });
  const queryClient = useQueryClient();

  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');
  const [receiveFiles, setReceiveFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docType, setDocType] = useState('bill');
  const [docFiles, setDocFiles] = useState([]);
  const [docsBusy, setDocsBusy] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const {
    data: orderActivities,
    refetch: refetchOrderActivities,
    isLoading
  } = useQuery({
    queryKey: ['orderActivities', id],
    queryFn: () => getOrderByOrderId(id),
    enabled: !!id
  });

  const { data: grnRes, isLoading: loadingGrns } = useQuery({
    queryKey: ['poGRNs', id],
    queryFn: () => getPoGRNs({ po_id: id, limit: 200, page: 1 }),
    enabled: !!id
  });

  const grnsByItem = useMemo(() => {
    const map = {};
    const rows = grnRes?.data || [];
    rows.forEach((grn) => {
      const key = grn.poItem_id;
      if (!map[key]) map[key] = [];
      map[key].push(grn);
    });
    return map;
  }, [grnRes]);

  const lineRows = useMemo(() => {
    return (orderActivities?.poItems || []).map((poItem) => {
      const prior = grnsByItem[poItem.id] || [];
      const receivedQty = prior.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
      const orderedQty = Number(poItem.quantity || 0);
      const remaining = Math.max(0, orderedQty - receivedQty);
      return {
        ...poItem,
        receivedQty,
        remaining,
        priorGrns: prior,
        canReceive: remaining > 0 && String(poItem.status || '').toLowerCase() !== 'completed'
      };
    });
  }, [grnsByItem, orderActivities]);

  useEffect(() => {
    mixpanel.track('Order Activity List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const openReceive = (row) => {
    setReceiveItem(row);
    setReceiveQty(String(row.remaining || ''));
    setReceiveFiles([]);
  };

  const closeReceive = () => {
    setReceiveItem(null);
    setReceiveQty('');
    setReceiveFiles([]);
  };

  const receiveMutation = useMutation({
    mutationFn: async () => {
      const qty = Number(receiveQty);
      if (!Number.isFinite(qty) || qty <= 0) {
        throw new Error('Enter a valid quantity');
      }
      if (qty > Number(receiveItem.remaining)) {
        throw new Error(`Only ${receiveItem.remaining} remaining`);
      }
      if (!receiveFiles.length) {
        throw new Error('Upload at least one photo as proof');
      }
      setSubmitting(true);
      const uploaded = await uploadFilesToS3(Array.from(receiveFiles));
      return createPoGRN({
        po_id: Number(id),
        poItem_id: receiveItem.id,
        quantity: qty,
        images: uploaded.map((file) => ({ name: file.name, url: file.url }))
      });
    },
    onSuccess: () => {
      toast.success('Material received recorded');
      mixpanel.track('PO GRN Created', {
        user_id: userId,
        po_id: Number(id),
        poItem_id: receiveItem?.id
      });
      closeReceive();
      refetchOrderActivities();
      queryClient.invalidateQueries({ queryKey: ['poGRNs', id] });
      queryClient.invalidateQueries({ queryKey: ['inventory-pipeline-orders'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to receive material');
    },
    onSettled: () => setSubmitting(false)
  });

  const documents = orderActivities?.documents || [];

  const openDocs = () => {
    setDocType('bill');
    setDocFiles([]);
    setDocsOpen(true);
  };

  const closeDocs = () => {
    setDocsOpen(false);
    setDocFiles([]);
  };

  const uploadDocsMutation = useMutation({
    mutationFn: async () => {
      const files = Array.from(docFiles || []);
      if (!files.length) {
        throw new Error('Choose at least one PDF or image');
      }
      const invalid = validatePODocumentFiles(files);
      if (invalid) {
        throw new Error(invalid);
      }
      setDocsBusy(true);
      return addPODocuments(id, await uploadPODocumentFiles(`po-${id}`, files, docType));
    },
    onSuccess: () => {
      toast.success('Document uploaded');
      mixpanel.track('PO Document Uploaded', {
        user_id: userId,
        po_id: Number(id),
        document_type: docType
      });
      closeDocs();
      refetchOrderActivities();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload document');
    },
    onSettled: () => setDocsBusy(false)
  });

  const deleteDocMutation = useMutation({
    mutationFn: (documentId) => {
      setDeletingDocId(documentId);
      return deletePODocument(id, documentId);
    },
    onSuccess: () => {
      toast.success('Document removed');
      refetchOrderActivities();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to remove document');
    },
    onSettled: () => setDeletingDocId(null)
  });

  const handleDeleteDoc = (doc) => {
    if (window.confirm(`Remove "${doc.name}" from this PO?`)) {
      deleteDocMutation.mutate(doc.id);
    }
  };

  return (
    <Layout>
      <Loader isVisible={isLoading || loadingGrns || submitting || docsBusy} />
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header data={{ name: 'Order Items' }} backArrow={true} onBack={onBack} />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <GridWrapper width={100}>
          {orderActivities?.quotation && (
            <div className="mt-[5rem] mb-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-gray-800">Order summary</h2>
                <span className="rounded-full bg-[#E9F5F6] px-3 py-1 text-xs font-semibold text-[#2A565E]">
                  {orderActivities.status || '—'}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-3 text-sm">
                  <p className="text-gray-500">Total amount</p>
                  <p className="font-semibold text-gray-900">
                    ₹
                    {new Intl.NumberFormat('en-IN').format(
                      orderActivities.quotation.total_amount || 0
                    )}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 text-sm">
                  <p className="text-gray-500">Tax type</p>
                  <p className="font-semibold text-gray-900">
                    {orderActivities.quotation.taxType || 'N/A'}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 text-sm">
                  <p className="text-gray-500">Created</p>
                  <p className="font-semibold text-gray-900">
                    {orderActivities.quotation.created_at
                      ? new Date(orderActivities.quotation.created_at).toLocaleDateString('en-IN')
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`${orderActivities?.quotation ? '' : 'mt-[5rem]'} mb-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-[#1E4A53]">Bills &amp; quotes</h3>
                <p className="text-xs text-gray-500">
                  Attach your own bill, quotation or other paperwork for this PO (PDF or image).
                </p>
              </div>
              <button
                type="button"
                onClick={openDocs}
                className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">
                Upload document
              </button>
            </div>
            {documents.length === 0 ? (
              <p className="mt-3 rounded-lg bg-gray-50 px-3 py-3 text-sm text-gray-500">
                No documents uploaded yet.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100">
                {documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="rounded-full bg-[#E9F5F6] px-2 py-0.5 text-[11px] font-semibold text-[#2A565E]">
                        {PO_DOCUMENT_TYPES.find((t) => t.value === doc.type)?.label || 'Other'}
                      </span>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate font-medium text-[#2A565E] underline">
                        {doc.name}
                      </a>
                      {doc.uploaded_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(doc.uploaded_at).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={deletingDocId === doc.id}
                      onClick={() => handleDeleteDoc(doc)}
                      className="text-xs font-medium text-red-600 hover:underline disabled:opacity-60">
                      {deletingDocId === doc.id ? 'Removing…' : 'Remove'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3">
              <h3 className="text-base font-semibold text-[#1E4A53]">Receive material on site</h3>
              <p className="text-xs text-gray-500">
                Enter received qty and upload photos. Stock updates when GRN is saved.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Ordered</th>
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3">Remaining</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lineRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                        No PO items found
                      </td>
                    </tr>
                  ) : (
                    lineRows.map((row) => (
                      <tr key={row.id} className="border-t border-gray-100 align-top">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{row.item?.item_name || '—'}</p>
                          <p className="text-xs text-gray-500">
                            {row.item?.category_name || '—'} · {row.item?.unit || '—'}
                          </p>
                          {row.priorGrns?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {row.priorGrns.map((grn) => (
                                <div
                                  key={grn.id}
                                  className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600">
                                  GRN #{grn.id}: {grn.quantity}
                                  {grn.images?.[0]?.url ? (
                                    <a
                                      href={grn.images[0].url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="ml-1 text-[#2A565E] underline">
                                      photo
                                    </a>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{row.quantity}</td>
                        <td className="px-4 py-3">{row.receivedQty}</td>
                        <td className="px-4 py-3">{row.remaining}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            {row.status || 'pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {row.canReceive ? (
                            <button
                              type="button"
                              onClick={() => openReceive(row)}
                              className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">
                              Receive
                            </button>
                          ) : (
                            <span className="text-xs text-emerald-700">Complete</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </GridWrapper>
      </div>

      {receiveItem && (
        <Modal name={`Receive — ${receiveItem.item?.item_name || 'Item'}`} width={42} onClose={closeReceive}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Ordered {receiveItem.quantity}. Already received {receiveItem.receivedQty}. Remaining{' '}
              <span className="font-semibold text-[#1E4A53]">{receiveItem.remaining}</span>.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Quantity received</span>
              <input
                type="number"
                min={1}
                max={receiveItem.remaining}
                value={receiveQty}
                onChange={(e) => setReceiveQty(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-[#2A565E]"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Photos (required)</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setReceiveFiles(e.target.files || [])}
                className="block w-full text-sm text-gray-600"
              />
              {receiveFiles?.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">{receiveFiles.length} file(s) selected</p>
              )}
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeReceive}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => receiveMutation.mutate()}
                className="rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {submitting ? 'Saving…' : 'Confirm receive'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {docsOpen && (
        <Modal name="Upload bill or quote" width={42} onClose={closeDocs}>
          <div className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Document type</span>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 outline-none focus:border-[#2A565E]">
                {PO_DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Files (PDF or image, up to {MAX_DOCUMENT_MB} MB each)
              </span>
              <input
                type="file"
                accept="application/pdf,image/*"
                multiple
                onChange={(e) => setDocFiles(e.target.files || [])}
                className="block w-full text-sm text-gray-600"
              />
              {docFiles?.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">{docFiles.length} file(s) selected</p>
              )}
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeDocs}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                Cancel
              </button>
              <button
                type="button"
                disabled={docsBusy}
                onClick={() => uploadDocsMutation.mutate()}
                className="rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {docsBusy ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default OrderItemsList;
